import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, FileText, Users, MessageSquare, GitBranch, Library, Sigma, Bookmark, Crown, Sparkles, Gamepad2, Bot, Network } from "lucide-react";
import { api, searchAllDossiers } from "../lib/api";

/**
 * Universal Cmd+K (or `/`) command palette.
 *
 * Searches dossiers and users in parallel via existing endpoints (no new
 * backend route). Static page entries are also included so users can jump to
 * any branded area with a keyword. Color of each entry mirrors the canonical
 * route palette so the result list teaches the color language passively.
 *
 * Triggers:
 *   - ⌘K / Ctrl+K (anywhere)
 *   - "/" key when not focused in an input/textarea
 *   - Custom DOM event `open-command-palette` (for buttons)
 * Close: Esc / overlay click / Enter selects.
 */

// Static "go to" entries — these match the canonical color palette.
const STATIC_ENTRIES = [
  { type: "page", title: "Archive", subtitle: "Curated dossiers across every category", url: "/archive", color: "#ff3b30", Icon: Library, keywords: "files dossiers categories" },
  { type: "page", title: "AI Deep Dive", subtitle: "Generate a full dossier from any topic", url: "/deep-dive", color: "#00ff88", Icon: Sparkles, keywords: "ai dive chat question" },
  { type: "page", title: "Connect Dots", subtitle: "Find the hidden connection between any 2-5 things", url: "/connect-dots", color: "#f472b6", Icon: GitBranch, keywords: "connection pattern dots" },
  { type: "page", title: "Equation Lab", subtitle: "LaTeX + Unicode math with live preview", url: "/equation", color: "#ffd60a", Icon: Sigma, keywords: "math equation latex unicode tensor fraction" },
  { type: "page", title: "Signal Board", subtitle: "Threaded discussions", url: "/threads", color: "#60a5fa", Icon: MessageSquare, keywords: "threads signal forum discussion" },
  { type: "page", title: "The Ties", subtitle: "Collaborative pin-string boards", url: "/warroom", color: "#22d3ee", Icon: Users, keywords: "ties warroom network map pin string" },
  { type: "page", title: "Sigils & Profile", subtitle: "Your sigils, public profile, vault ID card", url: "/profile", color: "#a78bfa", Icon: Crown, keywords: "profile sigils badges identity rank" },
  { type: "page", title: "Vault Cards", subtitle: "Daily collectible card pulls + trading", url: "/vault-cards", color: "#ff9500", Icon: Gamepad2, keywords: "cards game collect trade rare mythic classified daily pull" },
  { type: "page", title: "Saved Library", subtitle: "Bookmarks, AI dives, equations", url: "/bookmarks", color: "#e879f9", Icon: Bookmark, keywords: "saved bookmarks library" },
];

const ICON_BY_TYPE = {
  page: Library,
  dossier: FileText,
  user: Users,
};

// Debounce helper
const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [dossiers, setDossiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debouncedQ = useDebounced(q.trim(), 200);

  // Global open/close keyboard listener
  useEffect(() => {
    const onKey = (e) => {
      // Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      // "/" when not typing in a field
      if (e.key === "/" && !open) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        const isField = tag === "input" || tag === "textarea" || document.activeElement?.isContentEditable;
        if (!isField) {
          e.preventDefault();
          setOpen(true);
        }
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Custom event hook for buttons to open the palette
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-command-palette", h);
    return () => window.removeEventListener("open-command-palette", h);
  }, []);

  // Focus input when opened, clear when closed
  useEffect(() => {
    if (open) {
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ("");
      setDossiers([]);
      setUsers([]);
    }
  }, [open]);

  // Search fan-out — fires on debounced query change. Use the unified
  // searchAllDossiers helper so AI dossiers + connections are ALWAYS
  // included alongside curated dossiers. Never use /dossiers directly here.
  useEffect(() => {
    if (!open || !debouncedQ) {
      setDossiers([]);
      setUsers([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      searchAllDossiers(debouncedQ, 6).catch(() => []),
      api.get("/users/search", { params: { q: debouncedQ, limit: 6 } }).then((r) => r.data?.items || r.data || []).catch(() => []),
    ])
      .then(([d, u]) => {
        if (cancelled) return;
        setDossiers(Array.isArray(d) ? d : []);
        setUsers(Array.isArray(u) ? u : []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, debouncedQ]);

  // Merge results into one flat list for arrow-key nav.
  const items = useMemo(() => {
    const out = [];
    const lowerQ = debouncedQ.toLowerCase();
    // 1) Static pages — fuzzy match on title + keywords
    for (const p of STATIC_ENTRIES) {
      if (!lowerQ || p.title.toLowerCase().includes(lowerQ) || p.keywords.includes(lowerQ)) {
        out.push({ ...p });
      }
    }
    // 2) Dossiers + AI dossiers + connections — already normalized
    const TYPE_META = {
      dossier:    { Icon: FileText, color: "#ff3b30" },
      ai_dossier: { Icon: Bot,      color: "#a78bfa" },
      connection: { Icon: Network,  color: "#60a5fa" },
    };
    for (const d of dossiers) {
      const meta = TYPE_META[d.type] || TYPE_META.dossier;
      out.push({
        type: d.type,
        title: d.title,
        subtitle: d.subtitle || "",
        url: d.url,
        color: meta.color,
        Icon: meta.Icon,
      });
    }
    // 3) Users
    for (const u of users) {
      out.push({
        type: "user",
        title: `@${u.username}`,
        subtitle: u.bio ? u.bio.slice(0, 64) : "Public profile",
        url: `/u/${u.username}`,
        color: "#a78bfa",
        Icon: Users,
      });
    }
    return out;
  }, [debouncedQ, dossiers, users]);

  // Clamp active index whenever items change
  useEffect(() => {
    if (active >= items.length) setActive(Math.max(0, items.length - 1));
  }, [items.length, active]);

  const go = (item) => {
    setOpen(false);
    if (item?.url) navigate(item.url);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (items[active]) go(items[active]);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[200] bg-[#050505]/85 backdrop-blur-sm flex items-start justify-center p-4 sm:p-12"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-2xl bg-[#0a0a0a] border border-white/15 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top stripe — multi-color hint that this searches everything */}
        <div className="flex h-[3px]">
          {STATIC_ENTRIES.map((p) => (
            <span key={p.url} className="flex-1" style={{ backgroundColor: p.color }} />
          ))}
        </div>
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search size={16} className="text-zinc-500 shrink-0" />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onInputKey}
            placeholder="Search dossiers, users, areas… (⌘K · /)"
            className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder:text-zinc-600"
          />
          <kbd className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 border border-white/10 px-1.5 py-0.5">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto" data-testid="command-palette-results">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center font-mono text-[11px] tracking-[0.25em] text-zinc-600">
              {loading ? "// SEARCHING…" : debouncedQ ? "// NO RESULTS" : "// TYPE TO SEARCH"}
            </div>
          ) : (
            items.map((it, idx) => {
              const Icon = it.Icon || ICON_BY_TYPE[it.type] || ArrowRight;
              const isActive = idx === active;
              return (
                <button
                  key={`${it.type}-${it.url}-${idx}`}
                  data-testid={`command-palette-item-${idx}`}
                  data-active={isActive ? "true" : "false"}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => go(it)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-white/5 transition-colors ${
                    isActive ? "bg-white/[0.05]" : "hover:bg-white/[0.03]"
                  }`}
                >
                  <span
                    className="w-7 h-7 inline-flex items-center justify-center shrink-0 border"
                    style={{ borderColor: `${it.color}66`, color: it.color, backgroundColor: `${it.color}10` }}
                  >
                    <Icon size={13} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-mono text-[11px] tracking-[0.18em] uppercase truncate" style={{ color: it.color }}>
                      {it.title}
                    </span>
                    {it.subtitle ? (
                      <span className="block font-mono text-[10px] tracking-[0.1em] text-zinc-500 truncate">
                        {it.subtitle}
                      </span>
                    ) : null}
                  </span>
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-600 shrink-0">
                    {it.type}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between font-mono text-[9px] tracking-[0.25em] text-zinc-600 uppercase">
          <span>↑ ↓ Navigate · ↵ Open</span>
          <span>⌘K / "/" Anywhere</span>
        </div>
      </div>
    </div>
  );
}
